import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { Undo2 } from 'lucide-react';
import { Banner } from '@/components/Banner';
import { Button } from '@/components/Button';
import { Casilla } from '@/components/Casilla';
import { deshacerRestauracion } from '@/db/backup';
import { totalRegistros } from '@/db/schema';
import { mensajeDeError } from '@/db/tx';

interface Props {
  fechaCopia: string;
  /** Registros de la copia "antes de restaurar", sin contar los ajustes. */
  registrosCopia: number;
  /** Si se indica, quien lo usa muestra el resultado en lugar de este botón. */
  onDeshecho?: (registros: number) => void;
}

/**
 * "Deshacer restauración" con su confirmación (Req. 6.10, 8.10): vuelve a los
 * datos que había antes de la última restauración, que están en la copia
 * interna del teléfono.
 *
 * Muestra cuántos registros hay ahora y cuántos trae la copia. Si la copia
 * trae menos, pide una confirmación reforzada. Y antes de reemplazar se guarda
 * la copia "antes de deshacer", así que tampoco deshacer pierde datos.
 */
export function BotonDeshacer({ fechaCopia, registrosCopia, onDeshecho }: Props) {
  const registrosAhora = useLiveQuery(() => totalRegistros(), []);
  const [confirmando, setConfirmando] = useState(false);
  const [aceptaPerder, setAceptaPerder] = useState(false);
  const [trabajando, setTrabajando] = useState(false);
  const [resultado, setResultado] = useState<{ ok: boolean; texto: string } | null>(null);

  const ahora = registrosAhora ?? 0;
  const pierde = registrosCopia < ahora;

  async function deshacer() {
    setTrabajando(true);
    setResultado(null);
    try {
      const { registros } = await deshacerRestauracion();
      setConfirmando(false);
      setAceptaPerder(false);
      if (onDeshecho !== undefined) {
        onDeshecho(registros);
        return;
      }
      setResultado({ ok: true, texto: `Volviste a tus datos de antes: ${registros} registros.` });
    } catch (error) {
      setResultado({ ok: false, texto: mensajeDeError(error) });
    } finally {
      setTrabajando(false);
    }
  }

  return (
    <div className="space-y-2">
      {!confirmando ? (
        <Button
          variante="secundario"
          icono={Undo2}
          onClick={() => {
            setResultado(null);
            setConfirmando(true);
          }}
        >
          Deshacer restauración
        </Button>
      ) : (
        <Banner tono="aviso" titulo="¿Volver a los datos de antes de restaurar?">
          <p>
            Ahora hay {ahora} registros; la copia del {fechaCopia} tiene {registrosCopia}. Los
            datos actuales se reemplazan por los de la copia.
          </p>
          <p className="mt-2">
            Antes se guarda una copia de los datos actuales, por si registraste algo después de
            restaurar: la podrás descargar en Copias internas.
          </p>
          <div className="mt-3 space-y-2">
            {pierde && (
              <Casilla marcada={aceptaPerder} onCambio={setAceptaPerder}>
                Entiendo que la copia tiene menos registros que este teléfono ahora.
              </Casilla>
            )}
            <Button
              variante="peligro"
              disabled={trabajando || registrosAhora === undefined || (pierde && !aceptaPerder)}
              onClick={() => void deshacer()}
            >
              {trabajando ? 'Deshaciendo…' : 'Sí, deshacer'}
            </Button>
            <Button
              variante="secundario"
              disabled={trabajando}
              onClick={() => {
                setConfirmando(false);
                setAceptaPerder(false);
              }}
            >
              Cancelar
            </Button>
          </div>
        </Banner>
      )}
      {resultado !== null && (
        <Banner
          tono={resultado.ok ? 'exito' : 'alerta'}
          titulo={resultado.ok ? 'Restauración deshecha' : 'No se pudo deshacer'}
        >
          {resultado.texto}
        </Banner>
      )}
    </div>
  );
}
